import { isTourSearchResult, useTourTableData } from '../../onboarding/tableExercises';
import { useMemo, useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopSection } from '../../ui/TopSection';
import { useLoadOrderHistory, useLoadPriceAlerts, useLoadCostPrice, useLoadRevenueHistory, useLoadAccountProductFavorites } from '../../../utils/dataLoadingUtils';
import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { usePriceAlertsStore } from '../../../stores/appPriceAlertsStore';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { useAppUserDefaultStore, saveVatOrdersSetting } from '../../../stores/appUserDefaultStore';
import { useFiltersStore } from '../../../stores/appFiltersStore';
import { filterOrdersByDateRange } from '../../../utils/dateRangeFilters';
import { filterPriceAlertsByDateRange } from '../../../utils/priceAlertsCalculations';
import { getAvailablePlatforms, getAvailableCategories, getAvailableSubCategories, getFilteredSubCategories, filterOrdersByOverallType } from '../../orderhistory/utils';
import { useTimeControls } from '../../../hooks/useTimeControls';
import { StatCard } from '../../ui/StatCard';
import { Package, Download, MoreVertical, Loader2, Star, FileUp, Sparkles, ChevronRight, ChevronLeft, ClipboardCopy, ClipboardCheck, Check } from 'lucide-react';
import { metricIcon } from '../../ui/metricIcons';
import { usePrecomputedProductAnalytics, fastTopProductsAggregation } from '../../orderhistory/aggregations';
import { TableComponent } from '../../ui/TableComponent';
import { useOrderFilters } from '../../../hooks/useOrderFilters';
import { filterAllOrders } from '../../../utils/filterUtils';
import ProductCategoryModal from '../../orderhistory/ProductCategoryModal';
import ProductQuantityEditModal from '../../orderhistory/ProductQuantityEditModal';
import ProductNameEditModal from '../../orderhistory/ProductNameEditModal';
import ProductPriceEditModal from '../../orderhistory/ProductPriceEditModal';
import RestaurantNamesCell from '../../orderhistory/RestaurantNamesCell';
import { updateProductCategory, updateProductQuantity, updateProductName, updateProductPrice } from '../../../services/orderhistory/orderhistoryService';
import { useAuth } from '../../../services/authentication/authContext';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { normalizeDate } from '../../../utils/dateUtils';
import { resetScrollToTop } from '../../../utils/scrollUtils';
import { resolveProductIdentifier } from '../../../utils/platformUtils';
import { isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import { tertiaryText, smallTextSize, primaryText, mediumText, iconBgBlue, semiBoldText, getRankBadgeStyle } from '../../../styling/stylingHelper';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import RestaurantSelector from '../../common/RestaurantSelector';
import { ResponsiveTable, MobileCardList, MobileCardField } from '../../shared/mobile/ResponsiveTable';
import { SupplierLogo } from '../../ui/SupplierLogo';
import { currencyFormatter } from '../../../services/helperService';
import { useProductSearch } from '../../../hooks/useProductSearch';
import { canAccessDemoAccount, canAccessSourcingImprovement, canAccessSupplierComparison, canAccessCategoryCheck } from '../../../services/accessManagement/accessManagement';
import CategoryCheckModal, { CategoryCheckTarget } from './CategoryCheckModal';
import OrderHistorySourcingView from './OrderHistorySourcingView';
import SourcingRestaurantPicker from './SourcingRestaurantPicker';
import { useSourcingRunStore, sourcingAccessContext } from '../../../stores/appSourcingRunStore';
import { sourcingProductKey, SourcingProductRef } from '../../../services/sourcing/sourcingRunTypes';
import OrderHistorySupplierCompareView from './OrderHistorySupplierCompareView';
import CompareSupplierModal from './CompareSupplierModal';
import { SupplierComparisonResult } from '../../../services/sourcing/supplierComparisonService';
import { ProductAggregation } from '../../orderhistory/types';
import { formatAggregatedQuantity } from '../../../utils/quantityUtils';
import { Unit } from '../../../stores/appOrderHistoryStore';
import { Platform } from '../../../types/Platforms';
import { greenButtonClass, greyButtonClass } from '../../../styling/stylingHelper';
import type { ProductsExportMode, ProductsFileMode } from './productsExcelExport';
import { buildProductsClipboardText } from './productsExportRows';
import { FavoriteStar } from '../../ui/FavoriteStar';
import { updateRestaurantFavoriteProducts } from '../../../services/restaurant/restaurantService';


/**
 * The articleId to send to the server's update endpoints. The server matches
 * `(pd->>'articleId' = $2 OR pd->>'name' = $3)`, so a real article id targets the
 * product precisely, but a reused / sentinel id (catch-all "restgroep" ids,
 * `no_article_id`, name-keyed platforms) would OR-match every sibling line. Pass the
 * raw article id ONLY when it IS the product's resolved identity; otherwise pass
 * undefined so the server matches by name alone.
 */
function articleIdForUpdate(resolvedIdentifier: string, rawArticleId: string | undefined): string | undefined {
  return rawArticleId && resolvedIdentifier === rawArticleId ? rawArticleId : undefined;
}

/** Row caps offered in the export menu; null = every filtered product. */
const EXPORT_LIMITS: (number | null)[] = [25, 50, 100, 250, null];

/**
 * Favourite key for a products-table row, in the repo's canonical product-key shape
 * (`${platform}::${identifier}`, see `utils/reports/shared.ts`). The aggregation already
 * stores the RESOLVED identifier in `articleId`, so the resolver must NOT run again here
 * — re-resolving a resolved id would re-split catch-all ids and produce a different key.
 */
function favoriteKeyFor(row: { platform: string; articleId?: string }): string {
  return `${(row.platform || '').toLowerCase()}::${row.articleId ?? ''}`;
}

/** The three incompatible dimensions the Quantity column mixes (weight, volume, count). */
type QuantityDimension = 'weight' | 'volume' | 'piece';
const QUANTITY_DIMENSIONS: QuantityDimension[] = ['weight', 'volume', 'piece'];

/**
 * Which dimension a row's standardised quantity is in, or undefined when the row has no
 * quantity data. A row only ever carries ONE stdUnit — `aggregateQuantityInfos` majority-picks
 * it when a single product's own order lines disagree.
 */
function quantityDimensionOf(row: Pick<ProductAggregation, 'aggregatedQuantityInfo'>): QuantityDimension | undefined {
  const info = row?.aggregatedQuantityInfo;
  if (!info?.hasQuantityData) return undefined;
  switch (info.stdUnit) {
    case Unit.GRAM: return 'weight';
    case Unit.MILLILITER: return 'volume';
    case Unit.PIECE: return 'piece';
    default: return undefined;
  }
}

/**
 * The standardised quantity in BASE units (g / ml / pieces) — never the displayed number,
 * which `formatAggregatedQuantity` rescales above 1000 (2500 g renders as "2.5 kg", so the
 * shown number would sort 800 g above it).
 */
function quantityBaseValueFor(row: Pick<ProductAggregation, 'aggregatedQuantityInfo'>): number | undefined {
  const info = row?.aggregatedQuantityInfo;
  if (!info?.hasQuantityData) return undefined;
  return info.totalRawQuantity * info.weightedStdAmount;
}

/**
 * The number the "Orders" column shows: total raw ordered quantity, falling back to the
 * aggregation's line count. Returns undefined for rows the column renders as "--" so sorting
 * pushes them to the end instead of ranking them as 0. Used by BOTH the cell and the sort.
 */
function orderedQuantityFor(row: Pick<ProductAggregation, 'qty' | 'aggregatedQuantityInfo'>): number | undefined {
  const rawQuantity = row?.aggregatedQuantityInfo?.totalRawQuantity ?? row?.qty ?? 0;
  if (!(rawQuantity > 0 || row?.aggregatedQuantityInfo?.hasQuantityData)) return undefined;
  return rawQuantity;
}

// Selectable chips in the export menu (file mode + row cap).
const exportChipClass = "rounded-lg border px-2 py-1 text-[12px] font-medium transition-colors";
const exportChipActive = "border-transparent bg-[#0E8965] text-white";
const exportChipIdle = "border-[#E5E7EB] dark:border-borderPrimary-dark text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark";

// Rows in the "…" actions menu (sourcing actions + export options).
const menuItemClass = "w-full flex items-center gap-2 px-3 py-2 text-left text-[14px] leading-5 font-medium text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark";

// All products / Favorites segmented toggle, left of the search bar.
const favoritesToggleClass = "flex items-center rounded-[10px] px-3 py-1.5 text-[13px] font-medium transition-colors whitespace-nowrap";
const favoritesToggleActive = "bg-[#0E8965] text-white";
const favoritesToggleIdle = "text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark";

// Elevated card surface for the mobile card list (matches the shipped OrderHistoryPage cards).
const MOBILE_CARD =
  'bg-white dark:bg-card-dark rounded-xl p-4 shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]';

// How many mobile cards to reveal per "Load more" tap (never render the whole list at once).
const MOBILE_BATCH = 20;

const OrderHistoryProductsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, backendUser, accountSettings } = useAuth();

  // Reset scroll to top when this page loads
  useEffect(() => {
    resetScrollToTop();
  }, []);

  // Load data. Order history (the products table) loads progressively below, after the
  // time controls are known. Price alerts, cost price and revenue are secondary inputs
  // (utilization calc), so they keep their full loads.
  useLoadPriceAlerts();
  useLoadCostPrice();
  useLoadRevenueHistory();

  // Connect to stores
  const orderHistoryStore = useOrderHistory();
  const priceAlertsStore = usePriceAlertsStore();
  const costPriceStore = useCostPriceStore();
  const revenueHistoryStore = useRevenueHistory();
  const showExVatOrders = useAppUserDefaultStore(s => s.showExVatOrders);

  // Verify cost price and revenue data are accessible for utilization calculation
  useEffect(() => {
    if (costPriceStore.costPriceList.length > 0) {
      console.log('Cost price data loaded:', costPriceStore.costPriceList.length, 'analyses');
    }
    if (revenueHistoryStore.revenueHistoryList.length > 0) {
      console.log('Revenue history data loaded:', revenueHistoryStore.revenueHistoryList.length, 'entries');
    }
  }, [costPriceStore.costPriceList, revenueHistoryStore.revenueHistoryList]);
  const selectedRestaurant = useAppUserDefaultStore(s => s.restaurant);
  const restaurantId = selectedRestaurant?.id;
  const isAllRestaurants = selectedRestaurant && isAllRestaurantsSelected(selectedRestaurant);
  const platformToSectionIndex = useRestaurantStore(s => s.platformToSectionIndex);
  const restaurantSectionsMap = useRestaurantStore(s => s.restaurantSections);
  const platformMap = useRestaurantStore(s => s.restaurantSectionPlatforms);
  const restaurants = useRestaurantStore(s => s.restaurants);

  // Local state for category editing modal
  const [editingProduct, setEditingProduct] = useState<{
    name: string;
    category?: string;
    mainCategory?: string;
    subCategories?: string[];
    articleId?: string;
    restaurantPlatformId?: string
  } | null>(null);

  // Local state for quantity editing modal
  const [editingQuantity, setEditingQuantity] = useState<{
    name: string;
    quantity: number;
    unit: string;
    /**
     * Displayed units per one raw ordered unit (kg per pack, pieces per tray, ...). The modal edits
     * the number the Quantity column shows; the endpoint rewrites raw quantities. 1 when the row
     * has no quantity data and the column is already showing the raw count.
     */
    displayPerRaw: number;
    articleId?: string;
    restaurantPlatformId?: string;
  } | null>(null);

  // Local state for name editing modal
  const [editingName, setEditingName] = useState<{
    name: string;
    articleId?: string;
    restaurantPlatformId?: string;
  } | null>(null);

  // Local state for price editing modal
  const [editingPrice, setEditingPrice] = useState<{
    name: string;
    price: number;
    articleId?: string;
    restaurantPlatformId?: string;
  } | null>(null);

  // Local state for search
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Mobile-only: how many product cards are rendered (grows on "Load more").
  // The desktop table paginates internally; on mobile we window the same array.
  const [mobileVisible, setMobileVisible] = useState<number>(MOBILE_BATCH);

  // Account and restaurant favorites are independent, shared lists. The page
  // still lands on All products; choosing Favorites never requires starring first.
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const accountFavoritesContext = useLoadAccountProductFavorites(!!isAllRestaurants);
  const accountFavorites = useRestaurantStore(s => s.accountFavorites);
  const toggleAccountFavorite = useRestaurantStore(s => s.toggleAccountFavorite);
  const loadAccountFavorites = useRestaurantStore(s => s.loadAccountFavorites);
  const accountFavoritesCurrent = accountFavorites.context === accountFavoritesContext;
  const accountFavoritesReady = accountFavoritesCurrent && accountFavorites.loaded && !accountFavorites.loading;
  const reportSettings = useRestaurantStore(s => (restaurantId ? s.reportSettings[restaurantId] : undefined));
  const setReportSettings = useRestaurantStore(s => s.setReportSettings);
  const favoriteKeys = useMemo(() => isAllRestaurants
    ? accountFavoritesCurrent ? accountFavorites.keys : []
    : reportSettings?.productFavoriteProducts ?? [], [isAllRestaurants, accountFavoritesCurrent, accountFavorites.keys, reportSettings]);

  const handleToggleFavorite = useCallback(
    (key: string) => {
      if (isAllRestaurants) {
        void toggleAccountFavorite(accountFavoritesContext, key);
        return;
      }
      if (!restaurantId || !reportSettings) return;
      const next = favoriteKeys.includes(key) ? favoriteKeys.filter(k => k !== key) : [...favoriteKeys, key];
      setReportSettings(restaurantId, { ...reportSettings, productFavoriteProducts: next });
      updateRestaurantFavoriteProducts(restaurantId, next, 'products').catch((err) => {
        console.error('Failed to save favorite products', err);
        setReportSettings(restaurantId, { ...reportSettings, productFavoriteProducts: favoriteKeys });
      });
    },
    [restaurantId, reportSettings, favoriteKeys, setReportSettings, isAllRestaurants, accountFavoritesContext, toggleAccountFavorite]
  );

  // Quantity column: pick ONE unit dimension so the sort compares like with like. Lives in
  // the Quantity column's filter dropdown together with its sort control — that column can't
  // carry header chevrons without widening an already 10-column table.
  const [quantityDimension, setQuantityDimension] = useState<QuantityDimension | 'all'>('all');

  // Sourcing improvement state
  const [viewMode, setViewMode] = useState<'table' | 'sourcing' | 'compare'>('table');
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedProductKeys, setSelectedProductKeys] = useState<Set<string>>(new Set());
  const sourcingEntryKey = isAllRestaurants ? 'all' : (restaurantId || '');
  const sourcingRun = useSourcingRunStore(s => s.runs[sourcingEntryKey]);
  const [sourceRestaurantIds, setSourceRestaurantIds] = useState<string[]>([]);
  const [restartProducts, setRestartProducts] = useState<SourcingProductRef[]>([]);
  const [sourcingSetupError, setSourcingSetupError] = useState(false);
  const sourcingContext = sourcingAccessContext(user?.id, backendUser, restaurants.map(r => r.id));
  useEffect(() => { useSourcingRunStore.getState().setAccessContext(sourcingContext); }, [sourcingContext]);
  useEffect(() => {
    setSelectionMode(false); setSelectedProductKeys(new Set()); setRestartProducts([]); setViewMode('table');
  }, [sourcingEntryKey, sourcingContext]);

  // Supplier comparison: modal collects the file, result drives the compare view.
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [compareData, setCompareData] = useState<SupplierComparisonResult | null>(null);

  // "Check categories": a second AI model audits the categories on the rows currently on screen.
  const [categoryCheckOpen, setCategoryCheckOpen] = useState(false);

  // The "…" actions menu: supplier comparison, sourcing improvement and the
  // supplier-negotiation Excel exports (row cap, file mode, in-flight mode). Export is a
  // single line that drills into a second page of the menu, so the top level stays short.
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [menuView, setMenuView] = useState<'menu' | 'export'>('menu');
  const [exportingMode, setExportingMode] = useState<ProductsExportMode | null>(null);
  const [exportLimit, setExportLimit] = useState<number | null>(null);
  const [exportFileMode, setExportFileMode] = useState<ProductsFileMode>('overview');
  const [justCopied, setJustCopied] = useState(false);
  const actionsMenuRef = useRef<HTMLDivElement>(null);

  // Reset the "Copied!" confirmation shortly after it shows. The menu stays open so the user
  // actually sees it.
  useEffect(() => {
    if (!justCopied) return;
    const timer = setTimeout(() => setJustCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [justCopied]);

  // Always reopens on the top level — a closed menu should never resume mid-drilldown.
  const closeActionsMenu = useCallback(() => {
    setActionsMenuOpen(false);
    setMenuView('menu');
  }, []);

  useEffect(() => {
    if (!actionsMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsMenuRef.current && !actionsMenuRef.current.contains(event.target as Node)) {
        closeActionsMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actionsMenuOpen, closeActionsMenu]);

  // Check if user can edit quantities (demo account feature)
  const isQuantityEditAdmin = canAccessDemoAccount(backendUser, accountSettings);
  const hasSourcingAccess = canAccessSourcingImprovement(backendUser, accountSettings);
  const hasCompareAccess = canAccessSupplierComparison(backendUser, accountSettings);
  const hasCategoryCheckAccess = canAccessCategoryCheck(backendUser, accountSettings);

  // Use centralized filter store
  const overallTypeFilter = useFiltersStore(s => s.overallTypeFilter);
  const setOverallTypeFilter = useFiltersStore(s => s.setOverallTypeFilter);
  const restaurantFilters = useFiltersStore(s => s.restaurantFilters);
  const setRestaurantFilters = useFiltersStore(s => s.setRestaurantFilters);
  const sortColumn = useFiltersStore(s => s.orderHistoryProductsSortColumn);
  const sortDirection = useFiltersStore(s => s.orderHistoryProductsSortDirection);
  const setSort = useFiltersStore(s => s.setOrderHistoryProductsSort);

  // Reset filter to 'all' if current value is not available on this page
  useEffect(() => {
    const availableOptions = ['all', 'food', 'drinks', 'non-food'];
    if (overallTypeFilter && !availableOptions.includes(overallTypeFilter)) {
      setOverallTypeFilter('all');
    }
  }, [overallTypeFilter, setOverallTypeFilter]);

  // Order filters hook - include all filters for this page
  const {
    filters,
    setPlatformFilters,
    setSectionFilters,
    setCategoryFilters: baseSetCategoryFilters,
    setSubCategoryFilters,
    resetFilters,
    activeFilterCount,
  } = useOrderFilters({
    includeRestaurants: isAllRestaurants || false,
    includePlatforms: true,
    includeSections: true,
    includeCategories: true,
    includeSubCategories: true,
  });

  // Time controls - centralized logic. Declared before the data loading hook so the
  // progressive loader can prioritise the currently-viewed period.
  const {
    startDate,
    endDate,
    selectedTimeOption,
    dateRange,
    setDateRange,
    setSelectedTimeOption,
  } = useTimeControls();

  // Load order history progressively: the selected window (default last week) renders
  // first, then the rest of the history streams in the background.
  useLoadOrderHistory({ progressive: true, viewStart: startDate });

  // Progressive-loading state: "loading" means the currently viewed period hasn't fully
  // streamed in yet. The all-time view can only be trusted once everything is loaded.
  const ordersFirstChunkLoaded = useOrderHistory(s => s.firstChunkLoaded);
  const ordersFullyLoaded = useOrderHistory(s => s.fullyLoaded);
  const ordersLoadedFromDate = useOrderHistory(s => s.loadedFromDate);
  const isOrdersLoading =
    !ordersFullyLoaded && (
      !ordersFirstChunkLoaded ||
      selectedTimeOption === 'all' ||
      (!!ordersLoadedFromDate && startDate < ordersLoadedFromDate)
    );

  // Filter data by date range
  const dateFilteredOrders = useMemo(() =>
    filterOrdersByDateRange(orderHistoryStore.orderHistoryList, startDate, endDate),
    [orderHistoryStore.orderHistoryList, startDate, endDate]
  );

  // Handler for overall type filter changes
  const handleOverallTypeChange = (value: string) => {
    setOverallTypeFilter(value as 'all' | 'food' | 'drinks' | 'non-food');
  };

  // Handler for VAT toggle - updates both orders and revenue VAT settings
  const handleVatChange = async (value: string) => {
    const showExVat = value === 'excl';
    try {
      // Update both settings in parallel
      await Promise.all([
        saveVatOrdersSetting(showExVat)
      ]);
    } catch (error) {
      console.error('Failed to save VAT settings:', error);
    }
  };

  // Apply overall type filter, then all order filters (restaurants, sections, platforms, categories)
  const overallTypeFilteredOrders = useMemo(() =>
    filterOrdersByOverallType(dateFilteredOrders, overallTypeFilter),
    [dateFilteredOrders, overallTypeFilter]
  );

  const filteredOrders = useMemo(() =>
    filterAllOrders(
      overallTypeFilteredOrders,
      filters,
      isAllRestaurants ? restaurantFilters : undefined,
      platformToSectionIndex,
      filters.subCategories
    ),
    [overallTypeFilteredOrders, filters, isAllRestaurants, restaurantFilters, platformToSectionIndex]
  );

  const filteredPriceAlerts = useMemo(() =>
    filterPriceAlertsByDateRange(priceAlertsStore.priceAlerts, startDate, endDate),
    [priceAlertsStore.priceAlerts, startDate, endDate]
  );

  // Pre-compute analytics ONCE per period (massive performance boost!)
  const { avgSpentExVat, avgSpentIncVat, avgQuantity } = usePrecomputedProductAnalytics(
    orderHistoryStore.orderHistoryList,
    startDate,
    selectedTimeOption,
    selectionMode
  );

  // Use the fast aggregation with pre-computed maps
  const allProducts = useMemo(() => {
    const averageSpentMap = showExVatOrders ? avgSpentExVat : avgSpentIncVat;
    return fastTopProductsAggregation(
      filteredOrders,
      averageSpentMap,
      avgQuantity,
      filteredPriceAlerts,
      startDate,
      selectedTimeOption,
      showExVatOrders,
      selectionMode
    );
  }, [filteredOrders, showExVatOrders, avgSpentExVat, avgSpentIncVat, avgQuantity, filteredPriceAlerts, startDate, selectedTimeOption, selectionMode]);

  // Relevance search: every query token must actually be in the product, fuzzy only as a
  // bounded fallback. Plain fuzzy matched ~9 of 11 names for a 5-letter query ("gamba" hit
  // Frambozen, macadamia, Edamame...), which then flooded the KPIs and the table.
  // No cap: the KPI tiles, exports and supplier comparison all run on this list, so a
  // truncated result set would silently under-report Total Spent.
  const searchFilteredProducts = useProductSearch({
    data: allProducts,
    keys: ['articleId', 'name'],
    query: searchQuery,
    limit: Number.MAX_SAFE_INTEGER,
  }).results;

  // The Favorites view is a filter like any other: KPIs, exports, supplier comparison and
  // sourcing selection all run on this list, so what you see is what you act on.
  const favoriteScopedProducts = useMemo(() => {
    if (!favoritesOnly) return searchFilteredProducts;
    return searchFilteredProducts.filter(p => favoriteKeys.includes(favoriteKeyFor(p)));
  }, [searchFilteredProducts, favoritesOnly, favoriteKeys]);

  // The unit filter runs AFTER aggregation, unlike supplier/category which filter order lines
  // upstream. `aggregateQuantityInfos` majority-picks one unit for a product whose own lines
  // disagree, so dropping lines upstream would split such a product and recompute its spend
  // and price from a subset; filtering rows keeps every surviving row identical to the
  // unfiltered view. Like Favorites, it therefore also scopes the KPIs, export, sourcing and
  // supplier comparison — what you see is what you act on.
  const scopedProducts = useMemo(() => {
    if (quantityDimension === 'all') return favoriteScopedProducts;
    return favoriteScopedProducts.filter(p => quantityDimensionOf(p) === quantityDimension);
  }, [favoriteScopedProducts, quantityDimension]);

  // Offer only the dimensions actually present, so a meat-only view has no empty "Volume"
  // option. The active choice stays listed even once it matches nothing, so it's clearable.
  const availableQuantityDimensions = useMemo(() => {
    const present = new Set<QuantityDimension>();
    for (const product of favoriteScopedProducts) {
      const dimension = quantityDimensionOf(product);
      if (dimension) present.add(dimension);
    }
    if (quantityDimension !== 'all') present.add(quantityDimension);
    return QUANTITY_DIMENSIONS.filter(d => present.has(d));
  }, [favoriteScopedProducts, quantityDimension]);

  // Apply sorting (rows with missing values for the sorted field go to the end)
  const sortedProducts = useMemo(() => {
    if (!sortColumn || !sortDirection) return scopedProducts;

    const getSortValue = (row: ProductAggregation): number | undefined => {
      switch (sortColumn) {
        case 'spend': return row.spend;
        case 'price': return row.price;
        case 'priceAlert': return row.priceAlert;
        case 'unitPrice': return row.unitPrice?.value;
        case 'ordersCount': return orderedQuantityFor(row);
        case 'quantity': return quantityBaseValueFor(row);
        default: return undefined;
      }
    };

    // Pieces are counted, not measured, so their numbers are an order of magnitude smaller —
    // ascending by quantity would otherwise open with a wall of piece products. Keep them as a
    // trailing block in BOTH directions; grams and millilitres interleave (same magnitude), and
    // the unit filter is there when a strict like-for-like sort is wanted. No-op once the user
    // has filtered to pieces, since then every row is one.
    const isTrailingPieceRow = (row: ProductAggregation) =>
      sortColumn === 'quantity' && quantityDimensionOf(row) === 'piece';

    const dirMultiplier = sortDirection === 'asc' ? 1 : -1;
    return [...scopedProducts].sort((a, b) => {
      const aPiece = isTrailingPieceRow(a);
      const bPiece = isTrailingPieceRow(b);
      if (aPiece !== bPiece) return aPiece ? 1 : -1;
      const aVal = getSortValue(a);
      const bVal = getSortValue(b);
      const aMissing = aVal === undefined || aVal === null || Number.isNaN(aVal);
      const bMissing = bVal === undefined || bVal === null || Number.isNaN(bVal);
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      return (aVal! - bVal!) * dirMultiplier;
    });
  }, [scopedProducts, sortColumn, sortDirection]);

  // Add rank based on current sorted position (1-based)
  const rankedProducts = useMemo(() =>
    sortedProducts.map((product, index) => ({ ...product, rank: index + 1 })),
    [sortedProducts]
  );

  // Reset the mobile window whenever the ranked/filtered result set changes.
  useEffect(() => {
    setMobileVisible(MOBILE_BATCH);
  }, [rankedProducts]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSort(column, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // First click sorts highest-first.
      setSort(column, 'desc');
    }
  };

  const totalProducts = useMemo(() =>
    scopedProducts.length,
    [scopedProducts]
  );

  const totalSpentFiltered = useMemo(() => {
    return scopedProducts.reduce((acc, product) => acc + product.spend, 0);
  }, [scopedProducts]);

  // Get available filter options from restaurants (fixed list from store, not from filtered data)
  const availableRestaurants = useMemo(() =>
    isAllRestaurants ? restaurants.map(r => ({ id: r.id, name: r.name })) : [],
    [isAllRestaurants, restaurants]
  );

  // Get available sections from the restaurant store
  const availableSections = useMemo(() => {
    if (!restaurantId || !restaurantSectionsMap[restaurantId]) return [];
    return restaurantSectionsMap[restaurantId].map(s => ({ id: s.id, name: s.name }));
  }, [restaurantId, restaurantSectionsMap]);

  // Get available filter options from the overall-type-filtered data (before applying order filters)
  const availablePlatforms = useMemo(() =>
    getAvailablePlatforms(overallTypeFilteredOrders),
    [overallTypeFilteredOrders]
  );

  const availableCategories = useMemo(() =>
    getAvailableCategories(overallTypeFilteredOrders),
    [overallTypeFilteredOrders]
  );

  const allAvailableSubCategories = useMemo(() =>
    getAvailableSubCategories(overallTypeFilteredOrders),
    [overallTypeFilteredOrders]
  );

  // Filter subcategories based on selected categories
  const availableSubCategories = useMemo(() =>
    getFilteredSubCategories(
      filters.categories,
      allAvailableSubCategories,
      overallTypeFilteredOrders
    ),
    [filters.categories, allAvailableSubCategories, overallTypeFilteredOrders]
  );

  // Wrapper for setCategoryFilters that clears invalid subcategories
  const setCategoryFilters = useCallback((newCategories: string[]) => {
    baseSetCategoryFilters?.(newCategories);
    
    // Clear subcategories that are no longer valid for the selected categories
    if (setSubCategoryFilters && filters.subCategories.length > 0) {
      const validSubCategories = getFilteredSubCategories(
        newCategories,
        allAvailableSubCategories,
        overallTypeFilteredOrders
      );
      const filteredSubCategories = filters.subCategories.filter(subCat => 
        validSubCategories.includes(subCat)
      );
      if (filteredSubCategories.length !== filters.subCategories.length) {
        setSubCategoryFilters(filteredSubCategories);
      }
    }
  }, [baseSetCategoryFilters, setSubCategoryFilters, filters.subCategories, allAvailableSubCategories, overallTypeFilteredOrders]);

  const handleGoBack = () => {
    navigate("/orderhistory");
  };

  useLayoutEffect(() => {
    const search = (event: Event) => setSearchQuery((event as CustomEvent<string>).detail);
    window.addEventListener('onboarding:orders-search', search);
    return () => window.removeEventListener('onboarding:orders-search', search);
  }, []);

  useTourTableData('orders', { items: rankedProducts.map(product => ({ id: favoriteKeyFor(product), name: product.name })), ready: !isOrdersLoading, search: searchQuery });

  useLayoutEffect(() => {
    const clearSearch = () => setSearchQuery('');
    window.addEventListener('onboarding:orders-clear-search', clearSearch);
    return () => window.removeEventListener('onboarding:orders-clear-search', clearSearch);
  }, []);

  // "Clear filters" in the table's empty state. The table can only clear its own columns and
  // search, so this page takes over: the TopSection filters, the unit dimension and the
  // Favorites view can all empty the table too.
  const handleClearAllFilters = useCallback(() => {
    resetFilters();
    setQuantityDimension('all');
    setFavoritesOnly(false);
    setSearchQuery('');
  }, [resetFilters]);

  // Remove categoryOptions - no longer needed with new modal structure

  // Handler for category click
  const handleCategoryClick = (productName: string, currentCategory?: string) => {
    // Find the product to get its platform info
    const product = allProducts.find(p => p.name === productName);
    if (!product) {
      console.log('Product not found in allProducts:', productName);
      return;
    }

    // Find the order that contains this product to get restaurant_platform_id.
    // Match order lines by resolved product identity (the aggregation row stores the
    // RESOLVED identifier in product.articleId), so catch-all / sentinel / name-keyed
    // products resolve the same way here as in the aggregation.
    const orderWithProduct = dateFilteredOrders.find(order => {
      if (order.platform !== product.platform) return false;
      return order.productData.some(p =>
        resolveProductIdentifier(order.platform, p.articleId, p.name) === product.articleId
      );
    });

    if (!orderWithProduct) {
      console.log('Order not found for product:', productName, 'platform:', product.platform, 'articleId:', product.articleId);
      return;
    }

    // Find the actual product data to get mainCategory and subCategories
    const actualProduct = orderWithProduct.productData.find(p =>
      resolveProductIdentifier(orderWithProduct.platform, p.articleId, p.name) === product.articleId
    );

    setEditingProduct({
      name: productName,
      category: currentCategory,
      mainCategory: actualProduct?.mainCategory,
      subCategories: actualProduct?.subCategories || [],
      // Sent so the update matches articleId OR name — a line spelled differently on an older
      // invoice under the same articleId would otherwise keep the old category, and the row's
      // badge comes from the oldest line it aggregates.
      articleId: articleIdForUpdate(product.articleId, actualProduct?.articleId),
      restaurantPlatformId: orderWithProduct.restaurantPlatformId
    });
  };

  /**
   * Re-fetch the order history after a write, so the table shows what the DB now holds.
   *
   * `currentPlatformIds` has to be cleared first — `loadByPlatformIds` short-circuits on an
   * already-loaded platform set, so without this the "reload" is a no-op and the edited row keeps
   * its old badge until a full page refresh.
   *
   * The quantity / name / price handlers below still carry their own inline copy of this; folding
   * them in is a follow-up, not part of this change.
   */
  const reloadOrderHistory = useCallback(async () => {
    if (restaurantId && !isAllRestaurants) {
      const sections = restaurantSectionsMap[restaurantId] || [];
      const platformIds = sections.flatMap(s => {
        const platforms = platformMap[s.id] || [];
        return platforms.map(p => p.id);
      });

      useOrderHistory.setState({ currentPlatformIds: null });
      await orderHistoryStore.loadByPlatformIds(restaurantId, platformIds, platformToSectionIndex, backendUser);
    } else if (isAllRestaurants && restaurantId) {
      const allPlatformIds = restaurants.flatMap(restaurant => {
        const sections = restaurantSectionsMap[restaurant.id] || [];
        return sections.flatMap(s => {
          const platforms = platformMap[s.id] || [];
          return platforms.map(p => p.id);
        });
      });

      useOrderHistory.setState({ currentPlatformIds: null });
      await orderHistoryStore.loadByPlatformIds(restaurantId, allPlatformIds, platformToSectionIndex, backendUser);
    }
  }, [restaurantId, isAllRestaurants, restaurantSectionsMap, platformMap, platformToSectionIndex, backendUser, restaurants, orderHistoryStore]);

  // Handler for category update
  const handleUpdateCategory = async (mainCategory: string, category: string, subCategories: string[]) => {
    if (!editingProduct || !editingProduct.restaurantPlatformId) {
      console.error('Missing restaurant platform information');
      throw new Error('Missing restaurant platform information');
    }

    try {
      const result = await updateProductCategory(
        editingProduct.restaurantPlatformId,
        editingProduct.articleId,
        editingProduct.name,
        mainCategory,
        category,
        subCategories
      );

      await reloadOrderHistory();

      console.log(`Successfully updated ${result.updateCount} orders`);
    } catch (error) {
      console.error('Failed to update category:', error);
      throw error;
    }
  };

  // Handler for name click - edit for demo admins, copy articleId for others
  const handleNameClick = async (product: any) => {
    // For demo automation admins, open the name edit modal
    if (isQuantityEditAdmin) {
      // Locate the order line by resolved product identity (row.articleId is the resolved id).
      const orderWithProduct = dateFilteredOrders.find(order => {
        if (order.platform !== product.platform) return false;
        return order.productData.some(p =>
          resolveProductIdentifier(order.platform, p.articleId, p.name) === product.articleId
        );
      });

      if (!orderWithProduct) {
        console.log('Order not found for product:', product.name, 'platform:', product.platform);
        return;
      }

      const actualProduct = orderWithProduct.productData.find(p =>
        resolveProductIdentifier(orderWithProduct.platform, p.articleId, p.name) === product.articleId
      );

      setEditingName({
        name: product.name,
        articleId: articleIdForUpdate(product.articleId, actualProduct?.articleId),
        restaurantPlatformId: orderWithProduct.restaurantPlatformId
      });
    } else {
      // For non-demo accounts, copy articleId to clipboard
      if (product.articleId) {
        try {
          await navigator.clipboard.writeText(product.articleId);
          console.log('Article ID copied to clipboard:', product.articleId);
        } catch (error) {
          console.error('Failed to copy to clipboard:', error);
        }
      } else {
        console.log('No article ID available for this product');
      }
    }
  };

  // Handler for quantity click - only available for admin users
  const handleQuantityClick = (product: any) => {
    if (!isQuantityEditAdmin) {
      return;
    }

    // Locate the order line by resolved product identity (row.articleId is the resolved id).
    const orderWithProduct = dateFilteredOrders.find(order => {
      if (order.platform !== product.platform) return false;
      return order.productData.some(p =>
        resolveProductIdentifier(order.platform, p.articleId, p.name) === product.articleId
      );
    });

    if (!orderWithProduct) {
      console.log('Order not found for product:', product.name, 'platform:', product.platform);
      return;
    }

    const actualProduct = orderWithProduct.productData.find(p =>
      resolveProductIdentifier(orderWithProduct.platform, p.articleId, p.name) === product.articleId
    );

    // Edit in the unit the Quantity column shows ("224 kg"), not the raw pack count behind it —
    // taken from the aggregation rather than parsed back out of the label, which is rounded to one
    // decimal. `displayPerRaw` converts the entered number back on save.
    const info = product.aggregatedQuantityInfo;
    const totalRawQuantity = info?.totalRawQuantity ?? product.qty ?? 0;
    const formatted = info ? formatAggregatedQuantity(totalRawQuantity, info, product.category) : undefined;
    const displayValue = formatted?.value ?? totalRawQuantity;
    const displayPerRaw = totalRawQuantity !== 0 ? displayValue / totalRawQuantity : 1;

    setEditingQuantity({
      name: product.name,
      // Prefill what the column shows, so saving an untouched field is a no-op.
      quantity: Math.round(displayValue * 10) / 10,
      unit: formatted?.unit ?? '',
      displayPerRaw,
      articleId: articleIdForUpdate(product.articleId, actualProduct?.articleId),
      restaurantPlatformId: orderWithProduct.restaurantPlatformId
    });
  };

  // Handler for quantity update. `newQuantity` is in the unit the modal shows (kg / L / pieces);
  // the endpoint rescales the raw ordered quantities, so convert with the same factor the row was
  // formatted with — otherwise "150 kg" would be written as 150 packs.
  const handleUpdateQuantity = async (newQuantity: number) => {
    if (!editingQuantity || !editingQuantity.restaurantPlatformId) {
      console.error('Missing restaurant platform information');
      throw new Error('Missing restaurant platform information');
    }

    const newRawQuantity = editingQuantity.displayPerRaw !== 0
      ? newQuantity / editingQuantity.displayPerRaw
      : newQuantity;

    try {
      const result = await updateProductQuantity(
        editingQuantity.restaurantPlatformId,
        editingQuantity.articleId,
        editingQuantity.name,
        newRawQuantity,
        startDate,
        endDate
      );

      // Force reload by clearing current platform IDs first
      if (restaurantId && !isAllRestaurants) {
        const sections = restaurantSectionsMap[restaurantId] || [];
        const platformIds = sections.flatMap(s => {
          const platforms = platformMap[s.id] || [];
          return platforms.map(p => p.id);
        });

        // Clear the current platform IDs to force a fresh load
        useOrderHistory.setState({ currentPlatformIds: null });
        await orderHistoryStore.loadByPlatformIds(restaurantId, platformIds, platformToSectionIndex, backendUser);
      } else if (isAllRestaurants && restaurantId) {
        // For "All Restaurants", reload data for all restaurants
        const allPlatformIds = restaurants.flatMap(restaurant => {
          const sections = restaurantSectionsMap[restaurant.id] || [];
          return sections.flatMap(s => {
            const platforms = platformMap[s.id] || [];
            return platforms.map(p => p.id);
          });
        });

        // Clear the current platform IDs to force a fresh load
        useOrderHistory.setState({ currentPlatformIds: null });
        await orderHistoryStore.loadByPlatformIds(restaurantId, allPlatformIds, platformToSectionIndex, backendUser);
      }

      console.log(`Successfully updated ${result.updateCount} orders`);
    } catch (error) {
      console.error('Failed to update quantity:', error);
      throw error;
    }
  };

  // Handler for name update
  const handleUpdateName = async (newName: string) => {
    if (!editingName || !editingName.restaurantPlatformId) {
      console.error('Missing restaurant platform information');
      throw new Error('Missing restaurant platform information');
    }

    try {
      const result = await updateProductName(
        editingName.restaurantPlatformId,
        editingName.articleId,
        editingName.name,
        newName,
        startDate,
        endDate
      );

      // Force reload by clearing current platform IDs first
      if (restaurantId && !isAllRestaurants) {
        const sections = restaurantSectionsMap[restaurantId] || [];
        const platformIds = sections.flatMap(s => {
          const platforms = platformMap[s.id] || [];
          return platforms.map(p => p.id);
        });

        // Clear the current platform IDs to force a fresh load
        useOrderHistory.setState({ currentPlatformIds: null });
        await orderHistoryStore.loadByPlatformIds(restaurantId, platformIds, platformToSectionIndex, backendUser);
      } else if (isAllRestaurants && restaurantId) {
        // For "All Restaurants", reload data for all restaurants
        const allPlatformIds = restaurants.flatMap(restaurant => {
          const sections = restaurantSectionsMap[restaurant.id] || [];
          return sections.flatMap(s => {
            const platforms = platformMap[s.id] || [];
            return platforms.map(p => p.id);
          });
        });

        // Clear the current platform IDs to force a fresh load
        useOrderHistory.setState({ currentPlatformIds: null });
        await orderHistoryStore.loadByPlatformIds(restaurantId, allPlatformIds, platformToSectionIndex, backendUser);
      }

      console.log(`Successfully updated ${result.updateCount} orders`);
    } catch (error) {
      console.error('Failed to update name:', error);
      throw error;
    }
  };

  // Handler for price click - only available for admin users
  const handlePriceClick = (product: any) => {
    if (!isQuantityEditAdmin) {
      return;
    }

    // Locate the order line by resolved product identity (row.articleId is the resolved id).
    const orderWithProduct = dateFilteredOrders.find(order => {
      if (order.platform !== product.platform) return false;
      return order.productData.some(p =>
        resolveProductIdentifier(order.platform, p.articleId, p.name) === product.articleId
      );
    });

    if (!orderWithProduct) {
      console.log('Order not found for product:', product.name, 'platform:', product.platform);
      return;
    }

    const actualProduct = orderWithProduct.productData.find(p =>
      resolveProductIdentifier(orderWithProduct.platform, p.articleId, p.name) === product.articleId
    );

    setEditingPrice({
      name: product.name,
      price: product.price,
      articleId: articleIdForUpdate(product.articleId, actualProduct?.articleId),
      restaurantPlatformId: orderWithProduct.restaurantPlatformId
    });
  };

  // Handler for price update
  const handleUpdatePrice = async (newPrice: number) => {
    if (!editingPrice || !editingPrice.restaurantPlatformId) {
      console.error('Missing restaurant platform information');
      throw new Error('Missing restaurant platform information');
    }

    try {
      const result = await updateProductPrice(
        editingPrice.restaurantPlatformId,
        editingPrice.articleId,
        editingPrice.name,
        newPrice,
        startDate,
        endDate
      );

      // Force reload by clearing current platform IDs first
      if (restaurantId && !isAllRestaurants) {
        const sections = restaurantSectionsMap[restaurantId] || [];
        const platformIds = sections.flatMap(s => {
          const platforms = platformMap[s.id] || [];
          return platforms.map(p => p.id);
        });

        // Clear the current platform IDs to force a fresh load
        useOrderHistory.setState({ currentPlatformIds: null });
        await orderHistoryStore.loadByPlatformIds(restaurantId, platformIds, platformToSectionIndex, backendUser);
      } else if (isAllRestaurants && restaurantId) {
        // For "All Restaurants", reload data for all restaurants
        const allPlatformIds = restaurants.flatMap(restaurant => {
          const sections = restaurantSectionsMap[restaurant.id] || [];
          return sections.flatMap(s => {
            const platforms = platformMap[s.id] || [];
            return platforms.map(p => p.id);
          });
        });

        // Clear the current platform IDs to force a fresh load
        useOrderHistory.setState({ currentPlatformIds: null });
        await orderHistoryStore.loadByPlatformIds(restaurantId, allPlatformIds, platformToSectionIndex, backendUser);
      }

      console.log(`Successfully updated ${result.updateCount} orders`);
    } catch (error) {
      console.error('Failed to update price:', error);
      throw error;
    }
  };

  // Format platform options for the filter
  const formatPlatformName = (platform: string) => {
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  const formatPlatformOptions = (platforms: string[]) => {
    return platforms.map(p => ({ id: p, name: formatPlatformName(p) }));
  };

  // Sourcing selection helpers
  const getProductKey = (product: ProductAggregation) => sourcingProductKey(product.platform, product.articleId, product.unknownSupplierName);

  const toggleProductSelection = (product: ProductAggregation) => {
    const key = getProductKey(product);
    setSelectedProductKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  /**
   * The rows "Check categories" will audit: the on-screen list, so period, supplier, category,
   * search, unit and Favorites all scope it — same rule the export, sourcing and the KPIs follow.
   * The APPLY still spans the whole history for each accepted product; only the question is scoped.
   *
   * Each row needs the same three things `handleCategoryClick` digs out per click — the owning
   * restaurant_platform, the raw article id, and the line's current subcategories. Doing that with a
   * `.find()` per product is O(products × orders), which a 2000-product catalog feels; so the order
   * lines are indexed ONCE by resolved product identity here instead.
   */
  const categoryCheckTargets = useMemo<CategoryCheckTarget[]>(() => {
    const byIdentity = new Map<string, { restaurantPlatformId: string; rawArticleId?: string; subCategories: string[] }>();
    for (const order of dateFilteredOrders) {
      for (const line of order.productData || []) {
        // `product.articleId` on an aggregation row is the RESOLVED identifier, so resolve the raw
        // line the same way before keying — catch-all / sentinel / name-keyed ids collapse here.
        const key = `${order.platform}|${resolveProductIdentifier(order.platform, line.articleId, line.name)}`;
        if (byIdentity.has(key)) continue;
        byIdentity.set(key, {
          restaurantPlatformId: order.restaurantPlatformId,
          rawArticleId: line.articleId,
          subCategories: line.subCategories || [],
        });
      }
    }

    const targets: CategoryCheckTarget[] = [];
    for (const product of scopedProducts) {
      const match = byIdentity.get(`${product.platform}|${product.articleId}`);
      // No owning order line means no restaurant_platform to scope the update to — the same reason
      // the single-row category edit bails out.
      if (!match) continue;
      targets.push({
        rowKey: getProductKey(product),
        restaurantPlatformId: match.restaurantPlatformId,
        platform: product.platform,
        articleId: articleIdForUpdate(product.articleId, match.rawArticleId),
        productName: product.name,
        mainCategory: product.mainCategory,
        category: product.category,
        subCategories: match.subCategories,
        supplierName: product.unknownSupplierName,
      });
    }
    return targets;
  }, [scopedProducts, dateFilteredOrders]);

  const handleSelectAll = () => {
    const keys = new Set(scopedProducts.map(getProductKey));
    setSelectedProductKeys(keys);
  };

  const handleDeselectAll = () => {
    setSelectedProductKeys(new Set());
  };

  const handleOpenSourcing = () => {
    if (sourcingRun) {
      useSourcingRunStore.getState().open(sourcingEntryKey);
      setViewMode('sourcing');
      return;
    }
    setSourcingSetupError(false);
    setSourceRestaurantIds(restaurants.map(r => r.id));
    setRestartProducts([]);
    setSelectionMode(true);
  };

  const handleRestartSourcing = () => {
    if (!sourcingRun || sourcingRun.status === 'loading') return;
    setRestartProducts(sourcingRun.request.products);
    setSelectedProductKeys(new Set(sourcingRun.request.products.map(p => p.key)));
    if (isAllRestaurants) setRestaurantFilters(sourcingRun.request.targetRestaurantIds);
    setSourceRestaurantIds(sourcingRun.request.sourceFromRestaurantIds);
    if (sourcingRun.viewOption) setSelectedTimeOption(sourcingRun.viewOption);
    setDateRange({ start: new Date(sourcingRun.period.start), end: new Date(sourcingRun.period.end) });
    setSelectionMode(true);
    setViewMode('table');
  };

  const handleStartSourcing = () => {
    if (isOrdersLoading || sourcingRun?.status === 'loading' || !selectedProductKeys.size) return;
    const refs = new Map(restartProducts.map(p => [p.key, p]));
    for (const product of scopedProducts) {
      const key = getProductKey(product);
      refs.set(key, { key, platform: product.platform, articleId: product.articleId, name: product.name, supplierName: product.unknownSupplierName });
    }
    const products = Array.from(selectedProductKeys).map(key => refs.get(key)).filter((p): p is SourcingProductRef => !!p);
    const targetIds = isAllRestaurants
      ? restaurants.filter(r => restaurantFilters.length === 0 || restaurantFilters.includes(r.id)).map(r => r.id)
      : restaurantId ? [restaurantId] : [];
    if (!products.length || !targetIds.length) return;
    setSourcingSetupError(false);
    const platformIdsByRestaurant = Object.fromEntries(restaurants.map(r => [r.id,
      (restaurantSectionsMap[r.id] || []).flatMap(section => (platformMap[section.id] || []).map(platform => platform.id)),
    ]));
    const sourcingEnd = new Date(dateRange.end);
    sourcingEnd.setHours(23, 59, 59, 999);
    void useSourcingRunStore.getState().start(sourcingEntryKey, {
      version: 2, products, targetRestaurantIds: targetIds,
      sourceFromRestaurantIds: Array.from(new Set([...sourceRestaurantIds, ...targetIds])),
    }, { start: normalizeDate(dateRange.start).toISOString(), end: sourcingEnd.toISOString() }, platformIdsByRestaurant, selectedTimeOption).catch(error => { console.error('Could not start sourcing', error); setSourcingSetupError(true); setViewMode('table'); setSelectionMode(true); });
    setSelectionMode(false);
    setViewMode('sourcing');
  };

  const handleCancelSelection = () => {
    setSelectionMode(false);
    setSelectedProductKeys(new Set());
    setRestartProducts([]);
    if (sourcingRun) setViewMode('sourcing');
  };

  const handleBackFromSourcing = () => {
    setViewMode('table');
    setSelectionMode(false);
    setSelectedProductKeys(new Set());
  };

  const handleBackFromCompare = () => {
    setViewMode('table');
    setCompareData(null);
  };

  const handleCompareComplete = (result: SupplierComparisonResult) => {
    setCompareData(result);
    setCompareModalOpen(false);
    setViewMode('compare');
  };

  const showSourcingButton = hasSourcingAccess && viewMode === 'table';
  const showCompareButton = hasCompareAccess && !isAllRestaurants && viewMode === 'table';
  // Export is pure client-side, so it also works in the All Restaurants view.
  const showExportButton = hasSourcingAccess && viewMode === 'table';
  // Category checking works in the All Restaurants view too: the update it applies is account-wide
  // whichever view you start from, and every row carries its own restaurant_platform.
  const showCategoryCheckButton = hasCategoryCheckAccess && viewMode === 'table';
  // The "…" menu exists as soon as ANY of its entries is permitted; each entry keeps its
  // own gate inside, so consolidating them changes no visibility rule.
  const showActionsMenu = showSourcingButton || showCompareButton || showExportButton || showCategoryCheckButton;

  const showFavoritesToggle = !!restaurantId && (!isAllRestaurants || !!accountFavoritesContext);
  // Counted within the active filters + search + unit filter, so the badge matches what the
  // toggle shows.
  const favoriteCount = useMemo(
    () => searchFilteredProducts.filter(p =>
      favoriteKeys.includes(favoriteKeyFor(p)) &&
      (quantityDimension === 'all' || quantityDimensionOf(p) === quantityDimension)
    ).length,
    [searchFilteredProducts, favoriteKeys, quantityDimension]
  );

  const favoritesToggle = showFavoritesToggle ? (
    <div className="flex items-center gap-1 rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark p-0.5">
      <button
        type="button"
        onClick={() => setFavoritesOnly(false)}
        className={cn(favoritesToggleClass, !favoritesOnly ? favoritesToggleActive : favoritesToggleIdle)}
      >
        {t('products.favorites.allProducts')}
      </button>
      <button
        type="button"
        onClick={() => setFavoritesOnly(true)}
        disabled={!!isAllRestaurants && !accountFavoritesReady}
        className={cn(favoritesToggleClass, "gap-1.5", favoritesOnly ? favoritesToggleActive : favoritesToggleIdle)}
      >
        <Star className={cn("w-3.5 h-3.5", favoritesOnly ? "fill-white dark:fill-white" : "fill-[#F79009] dark:fill-[#F79009] text-[#F79009] dark:text-[#F79009]")} />
        {t('products.favorites.only')}
        <span className={favoritesOnly ? "opacity-80" : "opacity-60"}>{favoriteCount}</span>
      </button>
    </div>
  ) : undefined;

  /**
   * Excel export of the currently filtered products (search + all active filters), in one
   * of three shapes. The module (and exceljs) is dynamically imported so it stays out of
   * the main bundle until first use.
   */
  const handleExport = async (mode: ProductsExportMode) => {
    if (exportingMode) return;
    closeActionsMenu();
    setExportingMode(mode);
    try {
      const { exportProductsExcel } = await import('./productsExcelExport');
      await exportProductsExcel(t, mode, scopedProducts, {
        restaurantName: selectedRestaurant?.name || '',
        periodLabel: `${startDate.toLocaleDateString()} – ${endDate.toLocaleDateString()}`,
        typeFilterLabel: t(`overallType.${overallTypeFilter === 'non-food' ? 'nonFood' : overallTypeFilter}`),
        fileMode: exportFileMode,
        showExVat: showExVatOrders,
        includeRestaurants: !!isAllRestaurants,
        limit: exportLimit,
        generatedAt: new Date(),
      });
    } catch (error) {
      console.error('Failed to export products to Excel:', error);
    } finally {
      setExportingMode(null);
    }
  };

  /**
   * Clipboard export of the same rows, honouring the menu's file-mode and top-N chips. One flat
   * table, so the per-supplier / per-category shapes collapse to the 'all' column set. Imported
   * statically (no exceljs), keeping the write inside the click that triggered it.
   */
  const handleCopyProducts = async () => {
    try {
      await navigator.clipboard.writeText(
        buildProductsClipboardText(t, scopedProducts, {
          fileMode: exportFileMode,
          includeRestaurants: !!isAllRestaurants,
          limit: exportLimit,
        })
      );
      setJustCopied(true);
    } catch (error) {
      // Insecure context, denied permission or an unfocused document: no confirmation, no alert.
      console.error('Failed to copy products to clipboard:', error);
    }
  };

  // If in sourcing view, render the sourcing component
  if (viewMode === 'sourcing' && restaurantId) {
    return (
      <OrderHistorySourcingView
        entryKey={sourcingEntryKey}
        onRestart={handleRestartSourcing}
        onGoBack={handleBackFromSourcing}
      />
    );
  }

  // If in supplier-comparison view, show the results of comparing ALL filtered products
  // against the uploaded file. The "New file" action reopens the modal over this view.
  if (viewMode === 'compare' && restaurantId && compareData) {
    return (
      <>
        <OrderHistorySupplierCompareView
          products={scopedProducts}
          supplierName={compareData.supplierName}
          optionsByArticleId={compareData.optionsByArticleId}
          supplierProducts={compareData.supplierProducts}
          onGoBack={handleBackFromCompare}
          onNewFile={() => setCompareModalOpen(true)}
        />
        {compareModalOpen && (
          <CompareSupplierModal
            products={scopedProducts}
            restaurantId={restaurantId}
            onClose={() => setCompareModalOpen(false)}
            onComplete={handleCompareComplete}
          />
        )}
      </>
    );
  }

  return (
    <div data-onboarding="orders-products-page">
      {compareModalOpen && restaurantId && (
        <CompareSupplierModal
          products={scopedProducts}
          restaurantId={restaurantId}
          onClose={() => setCompareModalOpen(false)}
          onComplete={handleCompareComplete}
        />
      )}
      <MobilePageHeader title={t('orderHistory.products')} right={<RestaurantSelector />} />
      <TopSection
          title={t('orderHistory.products')}
          backButton={true}
          handleGoBack={handleGoBack}
          dateRange={dateRange}
          setDateRange={setDateRange}
          selectedTimeOption={selectedTimeOption}
          setSelectedTimeOption={setSelectedTimeOption}
          onOverallTypeChange={handleOverallTypeChange}
          currentOverallTypeValue={overallTypeFilter}
          overallTypeOptions={['all', 'food', 'drinks', 'non-food']}
          onVatChange={handleVatChange}
          currentVatValue={showExVatOrders ? 'excl' : 'incl'}
          showFilters={true}
          filters={{
            orderFilters: {
              restaurantFilters: isAllRestaurants ? restaurantFilters : undefined,
              setRestaurantFilters: isAllRestaurants ? setRestaurantFilters : undefined,
              availableRestaurants: isAllRestaurants ? availableRestaurants : undefined,
              platformFilters: filters.platforms,
              setPlatformFilters,
              availablePlatforms,
              sectionFilters: filters.sections,
              setSectionFilters,
              availableSections,
              categoryFilters: filters.categories,
              setCategoryFilters,
              availableCategories,
              subCategoryFilters: filters.subCategories,
              setSubCategoryFilters,
              availableSubCategories,
              onResetFilters: resetFilters,
              activeFilterCount,
            }
          }}
        />

        {/* Responsive layout group for content sections below 1250px */}
      <div className="flex flex-col gap-6 min-[1250px]:flex-col">
        {/* KPI Cards, row on >=1250px, column below */}
        <div className="flex flex-col gap-3 sm:gap-6 min-[1250px]:flex-row">
          <div className="w-full min-[1250px]:flex-1" data-onboarding="orders-products-spend" data-onboarding-ready={!isOrdersLoading}>
            <StatCard
              {...metricIcon('spend')}
              title={t('orderHistory.totalSpent')}
              value={totalSpentFiltered}
              valueType='currency'
              loading={isOrdersLoading}
            />
          </div>
          <div className="w-full min-[1250px]:flex-1">
            <StatCard
              icon={<Package className="w-6 h-6" style={{ color: '#2E90FA' }} />}
              iconBg={iconBgBlue}
              title={t('orderHistory.totalProducts')}
              value={totalProducts}
              valueType='number'
              loading={isOrdersLoading}
            />
          </div>
        </div>

      {sourcingSetupError && <p role="alert" className={cn(primaryText, smallTextSize)}>{t('products.sourcing.runError')}</p>}
      {/* Sourcing selection toolbar */}
      {selectionMode && (
        <div className="flex flex-wrap items-center justify-between gap-3 bg-background dark:bg-background-dark rounded-xl px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={handleSelectAll} className={cn(greyButtonClass, "text-sm py-1 px-3")}>
              {t('products.selectAll')}
            </button>
            <button onClick={handleDeselectAll} className={cn(greyButtonClass, "text-sm py-1 px-3")}>
              {t('products.deselectAll')}
            </button>
            <span className={cn(tertiaryText, smallTextSize)}>
              {selectedProductKeys.size} {t('products.selected')}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <SourcingRestaurantPicker restaurants={restaurants} selectedIds={sourceRestaurantIds} requiredIds={isAllRestaurants ? restaurants.filter(r => restaurantFilters.length === 0 || restaurantFilters.includes(r.id)).map(r => r.id) : restaurantId ? [restaurantId] : []} onChange={setSourceRestaurantIds} label={t('products.sourceFrom')} disabled={sourcingRun?.status === 'loading'} />
            <button onClick={handleCancelSelection} className={cn(greyButtonClass, "text-sm py-1 px-3")}>
              {t('products.cancel')}
            </button>
            <button
              onClick={handleStartSourcing}
              disabled={selectedProductKeys.size === 0 || isOrdersLoading || sourcingRun?.status === 'loading'}
              className={cn(greenButtonClass, "text-sm py-1 px-3", selectedProductKeys.size === 0 && "opacity-50 cursor-not-allowed")}
            >
              {t('products.analyze')}
            </button>
          </div>
        </div>
      )}

      {/* Responsive layout group for content sections below 1250px */}
      <div className="flex flex-col gap-6 min-[1250px]:flex-col">
        {/* products data for period */}
        <div className="w-full" data-onboarding="orders-products-table" data-onboarding-ready={!isOrdersLoading}>
            {isAllRestaurants && accountFavoritesCurrent && accountFavorites.error && (
              <p role="alert" className={cn(tertiaryText, smallTextSize, 'mb-3')}>
                {t(`products.favorites.${accountFavorites.error}`)}{' '}
                {accountFavorites.error === 'loadError' && (
                  <button type="button" className="underline" onClick={() => void loadAccountFavorites(accountFavoritesContext)}>
                    {t('common.retry')}
                  </button>
                )}
              </p>
            )}
            <ResponsiveTable
              desktop={
            <TableComponent
              title={t('orderHistory.productDetails')}
              data={rankedProducts}
              rowOnboardingId={row => isTourSearchResult('orders', favoriteKeyFor(row), searchQuery) ? "orders-search-result" : undefined}
              blankLines={false}
              usePagination={true}
              onCategoryClick={handleCategoryClick}
              onNameClick={handleNameClick}
              onQuantityClick={isQuantityEditAdmin ? handleQuantityClick : undefined}
              onPriceClick={isQuantityEditAdmin ? handlePriceClick : undefined}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              // Favorites-only counts as a filter here: it can empty the table on its own.
              activeFilterCount={activeFilterCount + (favoritesOnly ? 1 : 0)}
              onClearFilters={handleClearAllFilters}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder={t('orderHistory.searchByProductName')}
              searchOnboardingId="orders-products-search"
              subHeader={favoritesOnly && rankedProducts.length === 0 ? (
                <p className={cn(tertiaryText, smallTextSize)}>{t('products.favorites.explainer')}</p>
              ) : undefined}
              headerActions={favoritesToggle}
              headerActionsRight={showActionsMenu && !selectionMode ? (
                <div ref={actionsMenuRef} className="relative">
                  <button
                    type="button"
                    onClick={() => (actionsMenuOpen ? closeActionsMenu() : setActionsMenuOpen(true))}
                    disabled={exportingMode !== null}
                    aria-haspopup="menu"
                    aria-expanded={actionsMenuOpen}
                    aria-label={t('products.actions.menuLabel')}
                    className={cn(
                      "flex h-9 w-9 items-center justify-center rounded-xl border border-[#D0D5DD] dark:border-borderPrimary-dark bg-white dark:bg-background-dark text-[#344054] dark:text-textSecondary-dark hover:bg-[#F9FAFB] dark:hover:bg-backgroundDarker-dark transition-colors",
                      exportingMode !== null && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    {exportingMode ? <Loader2 className="w-5 h-5 animate-spin" /> : <MoreVertical className="w-5 h-5" />}
                  </button>

                  {actionsMenuOpen && (
                    <div
                      role="menu"
                      className="absolute right-0 top-full mt-1 min-w-[280px] rounded-xl border border-[#E5E7EB] dark:border-borderPrimary-dark bg-white dark:bg-background-dark shadow-lg py-1 z-50"
                    >
                      {menuView === 'menu' ? (
                        /* Top level: one line per action, each still gated by its own permission. */
                        <>
                          {showCompareButton && (
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                closeActionsMenu();
                                setCompareModalOpen(true);
                              }}
                              className={menuItemClass}
                            >
                              <FileUp className="w-4 h-4 shrink-0" />
                              {t('products.compareSupplier.button')}
                            </button>
                          )}
                          {showSourcingButton && (
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                closeActionsMenu();
                                handleOpenSourcing();
                              }}
                              className={menuItemClass}
                            >
                              <Sparkles className="w-4 h-4 shrink-0" />
                              {t('products.improveSourcing')}
                            </button>
                          )}
                          {showCategoryCheckButton && (
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                closeActionsMenu();
                                setCategoryCheckOpen(true);
                              }}
                              className={menuItemClass}
                            >
                              <ClipboardCheck className="w-4 h-4 shrink-0" />
                              {t('products.categoryCheck.menuItem')}
                            </button>
                          )}
                          {showExportButton && (
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => setMenuView('export')}
                              className={menuItemClass}
                            >
                              <Download className="w-4 h-4 shrink-0" />
                              <span className="flex-1">{t('products.export.menuItem')}</span>
                              <ChevronRight className="w-4 h-4 shrink-0" />
                            </button>
                          )}
                        </>
                      ) : (
                        /* Second level: pick file type + row cap, then trigger one of the exports. */
                        <>
                          <button
                            type="button"
                            onClick={() => setMenuView('menu')}
                            className={cn(menuItemClass, tertiaryText, "border-b border-[#E5E7EB] dark:border-borderPrimary-dark")}
                          >
                            <ChevronLeft className="w-4 h-4 shrink-0" />
                            {t('common.back')}
                          </button>

                          {/* File mode + row cap: both apply to whichever export is picked below. */}
                          <div className="px-3 py-2 border-b border-[#E5E7EB] dark:border-borderPrimary-dark">
                            <div className={cn(tertiaryText, smallTextSize, "mb-2")}>{t('products.export.fileModeLabel')}</div>
                            <div className="flex flex-wrap gap-1.5">
                              {(['overview', 'supplier'] as const).map(fileMode => (
                                <button
                                  key={fileMode}
                                  type="button"
                                  onClick={() => setExportFileMode(fileMode)}
                                  title={t(fileMode === 'overview' ? 'products.export.fileModeOverviewHint' : 'products.export.fileModeSupplierHint')}
                                  className={cn(exportChipClass, exportFileMode === fileMode ? exportChipActive : exportChipIdle)}
                                >
                                  {t(fileMode === 'overview' ? 'products.export.fileModeOverview' : 'products.export.fileModeSupplier')}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="px-3 py-2 border-b border-[#E5E7EB] dark:border-borderPrimary-dark">
                            <div className={cn(tertiaryText, smallTextSize, "mb-2")}>{t('products.export.limitLabel')}</div>
                            <div className="flex flex-wrap gap-1.5">
                              {EXPORT_LIMITS.map(limit => (
                                <button
                                  key={String(limit)}
                                  type="button"
                                  onClick={() => setExportLimit(limit)}
                                  className={cn(exportChipClass, exportLimit === limit ? exportChipActive : exportChipIdle)}
                                >
                                  {limit === null ? t('products.export.limitAll') : t('products.export.limitTop', { n: limit })}
                                </button>
                              ))}
                            </div>
                          </div>

                          {([
                            ['all', 'products.export.optionAll'],
                            ['bySupplier', 'products.export.optionBySupplier'],
                            ['byCategory', 'products.export.optionByCategory'],
                          ] as const).map(([mode, labelKey]) => (
                            <button
                              key={mode}
                              type="button"
                              role="menuitem"
                              onClick={() => handleExport(mode)}
                              className={menuItemClass}
                            >
                              <Download className="w-4 h-4 shrink-0" />
                              {t(labelKey)}
                            </button>
                          ))}

                          {/* Same rows as the files above, as pasteable text rather than a download. */}
                          <button
                            type="button"
                            role="menuitem"
                            onClick={handleCopyProducts}
                            className={cn(menuItemClass, "border-t border-[#E5E7EB] dark:border-borderPrimary-dark")}
                          >
                            {justCopied
                              ? <Check className="w-4 h-4 shrink-0 text-[#0E8965] dark:text-darkGreenColor-dark" />
                              : <ClipboardCopy className="w-4 h-4 shrink-0" />}
                            {justCopied ? t('products.export.copied') : t('products.export.copyToClipboard')}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              ) : undefined}
              columns={[
                {
                  key: 'name',
                  onboardingColumnId: 'orders-column-name',
                  label: t('dashboard.product'),
                  columnType: 'name',
                  render: (value: string, row: any) => {
                    const rank = row.rank || 1;
                    const getRankStyle = (rank: number) => {
                      if (rank === 1) return { background: 'linear-gradient(180deg, #0E8965 0%, #4AC4A0 100%)' };
                      if (rank === 2) return { background: 'linear-gradient(180deg, #7F56D9 0%, #9F73FF 100%)' };
                      if (rank === 3) return { background: 'linear-gradient(180deg, #F4B857 0%, #F48357 100%)' };
                      return { background: 'linear-gradient(180deg, #8C97A6 0%, #818A97 100%)' };
                    };

                    return (
                      <div className="flex items-center gap-2">
                        {selectionMode && (
                          <input
                            type="checkbox"
                            aria-label={row.name}
                            checked={selectedProductKeys.has(getProductKey(row))}
                            onChange={() => toggleProductSelection(row)}
                            className="w-4 h-4 rounded border-borderGrey dark:border-borderGrey-dark cursor-pointer flex-shrink-0"
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        {showFavoritesToggle && (
                          <FavoriteStar
                            isFavorite={favoriteKeys.includes(favoriteKeyFor(row))}
                            disabled={!!isAllRestaurants && (!accountFavoritesReady || accountFavorites.pending.includes(favoriteKeyFor(row)))}
                            onToggle={() => handleToggleFavorite(favoriteKeyFor(row))}
                          />
                        )}
                        <div
                          className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[11px] leading-[14px] tracking-[0.1px] font-bold flex-shrink-0"
                          style={getRankStyle(rank)}
                        >
                          {rank}
                        </div>
                        <span
                          className={cn(primaryText, smallTextSize, mediumText, isQuantityEditAdmin && "cursor-pointer hover:underline")}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNameClick(row);
                          }}
                          title={isQuantityEditAdmin ? "Click to edit name" : undefined}
                        >
                          {String(value)}
                        </span>
                      </div>
                    );
                  }
                },
                // Conditionally add restaurant column when "All Restaurants" is selected
                ...(isAllRestaurants ? [{
                  key: 'restaurantNames' as any,
                  label: t('orderHistory.restaurants') || 'Restaurants',
                  render: (value: any) => <RestaurantNamesCell restaurantNames={value} />,
                  filter: {
                    selectedValues: restaurantFilters,
                    onValuesChange: setRestaurantFilters,
                    availableOptions: availableRestaurants,
                    valueType: 'default' as const,
                    filterLabel: t('orderHistory.restaurants') || 'Restaurants',
                  }
                }] : []),
                {
                  key: 'platform',
                  onboardingId: 'orders-supplier-header orders-filter-header', onboardingColumnId: 'orders-column-supplier',
                  label: t('orderHistory.supplierTableHeader'),
                  columnType: 'platform',
                  filter: {
                    selectedValues: filters.platforms,
                    onValuesChange: setPlatformFilters,
                    availableOptions: formatPlatformOptions(availablePlatforms),
                    valueType: 'default',
                    filterLabel: 'Suppliers',
                  }
                },
                {
                  key: 'category',
                  onboardingId: 'orders-category-header orders-filter-header', onboardingColumnId: 'orders-column-category',
                  label: t('orderHistory.category'),
                  columnType: 'category',
                  filter: {
                    selectedValues: filters.categories,
                    onValuesChange: setCategoryFilters,
                    availableOptions: availableCategories,
                    valueType: 'category',
                    filterLabel: 'Categories',
                    maxHeight: 'max-h-[170px]',
                    subCategorySelectedValues: filters.subCategories,
                    subCategoryOnValuesChange: setSubCategoryFilters,
                    subCategoryAvailableOptions: availableSubCategories,
                    subCategoryFilterLabel: 'Subcategories',
                  }
                },
                { key: 'priceAlert', label: t('orderHistory.alerts'), columnType: 'priceAlert', sortable: true, sortKey: 'priceAlert', onboardingId: 'orders-sort-headers' },
                {
                  key: 'ordersCount' as any,
                  label: t('orderHistory.orders'),
                  sortable: true,
                  sortKey: 'ordersCount',
                  render: (_value: any, row: any) => {
                    const rawQuantity = orderedQuantityFor(row);
                    if (rawQuantity === undefined) {
                      return <span className={cn(tertiaryText, smallTextSize)}>--</span>;
                    }
                    const formatted = rawQuantity % 1 === 0 ? rawQuantity : rawQuantity.toFixed(1);
                    return (
                      <span className={cn(primaryText, smallTextSize, mediumText)}>
                        {formatted}
                      </span>
                    );
                  }
                },
                { key: 'price', label: t('orderHistory.price'), columnType: 'boldPrice', sortable: true, sortKey: 'price', onboardingId: 'orders-sort-headers', onboardingColumnId: 'orders-column-price' },
                { key: 'unitPrice', label: t('orderHistory.unitPrice'), columnType: 'unitPrice', sortable: true, sortKey: 'unitPrice', onboardingId: 'orders-sort-headers' },
                {
                  key: 'formattedQuantity',
                  onboardingColumnId: 'orders-column-quantity',
                  label: t('orderHistory.quantity'),
                  columnType: 'blueQuantity',
                  // Unit filter + sort live together in this column's dropdown: quantities in
                  // g, ml and pieces aren't comparable, so filtering to one dimension first is
                  // what makes the sort mean something.
                  filter: {
                    choice: {
                      label: t('products.quantityUnit.label'),
                      value: quantityDimension,
                      onChange: (value: string) => setQuantityDimension(value as QuantityDimension | 'all'),
                      options: [
                        { value: 'all', label: t('products.quantityUnit.all') },
                        ...availableQuantityDimensions.map(d => ({ value: d, label: t(`products.quantityUnit.${d}`) })),
                      ],
                    },
                    sort: {
                      label: t('products.quantitySort.label'),
                      // 'none' falls back to the aggregation's own spend-descending order.
                      value: sortColumn === 'quantity' ? (sortDirection ?? 'none') : 'none',
                      onChange: (value: string) =>
                        value === 'none' ? setSort(null, null) : setSort('quantity', value as 'asc' | 'desc'),
                      options: [
                        { value: 'none', label: t('products.quantitySort.none') },
                        { value: 'desc', label: t('products.quantitySort.highest') },
                        { value: 'asc', label: t('products.quantitySort.lowest') },
                      ],
                    },
                  },
                },
                { key: 'averageQuantity', label: t('orderHistory.avgQuantity'), columnType: 'base' },
                { key: 'spend', label: t('orderHistory.totalSpent'), columnType: 'currencyHighlightBlack', sortable: true, sortKey: 'spend', onboardingColumnId: 'orders-column-spend' },
              ]}
            />
              }
              mobile={
                <div className="w-full">
                  <h3 className={cn('mb-4 text-[18px] leading-6', semiBoldText, primaryText)}>{t('orderHistory.productDetails')}</h3>
                  {showSourcingButton && !selectionMode && <button type="button" onClick={handleOpenSourcing} className={cn(greyButtonClass, 'mb-3 flex items-center gap-2')}><Sparkles className="h-4 w-4" />{t('products.improveSourcing')}</button>}
                  {showFavoritesToggle && <div className="mb-3 flex">{favoritesToggle}</div>}
                  {/* Search — same state the desktop TableComponent search uses */}
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t('orderHistory.searchByProductName')}
                    className={cn('w-full h-10 px-3 mb-3 border rounded-lg text-[14px] bg-white dark:bg-card-dark border-[#D0D5DD] dark:border-borderPrimary-dark', primaryText)}
                  />
                  <MobileCardList
                    items={rankedProducts.slice(0, mobileVisible)}
                    keyOf={(row, i) => `${row.articleId ?? ''}-${row.platform ?? ''}-${i}`}
                    onLoadMore={() => setMobileVisible((v) => v + MOBILE_BATCH)}
                    hasMore={mobileVisible < rankedProducts.length}
                    loadMoreLabel={t('orderHistory.loadMore')}
                    renderCard={(row) => (
                      <div className={cn(MOBILE_CARD, 'flex flex-col gap-3')}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            {selectionMode && <input type="checkbox" aria-label={row.name} checked={selectedProductKeys.has(getProductKey(row))} onChange={() => toggleProductSelection(row)} className="h-4 w-4 shrink-0" />}
                            <div
                              className="w-5 h-5 rounded-full flex items-center justify-center text-white dark:text-white text-[11px] font-bold flex-shrink-0"
                              style={getRankBadgeStyle(row.rank || 1)}
                            >
                              {row.rank || 1}
                            </div>
                            {showFavoritesToggle && (
                              <FavoriteStar
                                isFavorite={favoriteKeys.includes(favoriteKeyFor(row))}
                                disabled={!!isAllRestaurants && (!accountFavoritesReady || accountFavorites.pending.includes(favoriteKeyFor(row)))}
                                onToggle={() => handleToggleFavorite(favoriteKeyFor(row))}
                              />
                            )}
                            <span className={cn('text-[14px] leading-5 font-medium truncate', primaryText)}>{row.name}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className={cn('text-xs font-medium', tertiaryText)}>{t('orderHistory.totalSpent')}</span>
                            <span className={cn('text-base font-bold', primaryText)}>{currencyFormatter.format(row.spend || 0)}</span>
                          </div>
                        </div>
                        <div className="flex items-stretch border-t border-border dark:border-border-dark pt-3">
                          <MobileCardField
                            label={t('orderHistory.supplierTableHeader')}
                            value={<SupplierLogo platform={row.platform as Platform} unknownSupplierName={row.unknownSupplierName} size="small" />}
                            divider
                          />
                          <MobileCardField label={t('orderHistory.price')} value={currencyFormatter.format(row.price || 0)} divider />
                          <MobileCardField label={t('orderHistory.quantity')} value={row.formattedQuantity || '--'} />
                        </div>
                      </div>
                    )}
                  />
                </div>
              }
            />
          </div>
      </div>
    </div>

    {/* Check categories: AI audit of the on-screen rows, applied only on the user's say-so */}
    {categoryCheckOpen && (
      <CategoryCheckModal
        isOpen={categoryCheckOpen}
        onClose={() => setCategoryCheckOpen(false)}
        // Attribution only — the All Restaurants selection is a sentinel id, not a restaurant.
        restaurantId={isAllRestaurants ? null : (restaurantId ?? null)}
        targets={categoryCheckTargets}
        onApplied={reloadOrderHistory}
      />
    )}

    {/* Category Edit Modal */}
    {editingProduct && (
      <ProductCategoryModal
        productName={editingProduct.name}
        currentCategory={editingProduct.category}
        currentMainCategory={editingProduct.mainCategory}
        currentSubCategories={editingProduct.subCategories}
        onSelectCategory={handleUpdateCategory}
        onClose={() => setEditingProduct(null)}
      />
    )}

    {/* Quantity Edit Modal */}
    {editingQuantity && (
      <ProductQuantityEditModal
        productName={editingQuantity.name}
        currentQuantity={editingQuantity.quantity}
        unit={editingQuantity.unit}
        onUpdateQuantity={handleUpdateQuantity}
        onClose={() => setEditingQuantity(null)}
      />
    )}

    {/* Name Edit Modal */}
    {editingName && (
      <ProductNameEditModal
        currentName={editingName.name}
        onUpdateName={handleUpdateName}
        onClose={() => setEditingName(null)}
      />
    )}

    {/* Price Edit Modal */}
    {editingPrice && (
      <ProductPriceEditModal
        productName={editingPrice.name}
        currentPrice={editingPrice.price}
        onUpdatePrice={handleUpdatePrice}
        onClose={() => setEditingPrice(null)}
      />
    )}
    </div>
  );
};

export default OrderHistoryProductsPage;
